import React from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Icon,
  Link,
  TranslationProvider,
  VenueLocationSummary,
} from '@resy/ui-kitchen';

import ButtonDropdown from '../ButtonDropdown';
import Calendar from '../../images/amexoffers/icons/calendar-check.svg';
import GreenCheck from '../../../images/icons/check.svg';
import People from '../../../images/icons/people.svg';

function getCalendarLinks(reservation) {
  return Object.keys(reservation?.links?.calendar).map((cal) => (
    <TranslationProvider
      translationKey={cal}
    >
      <Link href={reservation.links.calendar[cal]} target="_blank" rel="noopener noreferrer">{cal}</Link>
    </TranslationProvider>
  ));
}

export default function RSVPConfirmed({
  reservation,
  seating,
  serviceType,
  testId,
  toggleDeclineModal,
}) {
  const toggleButtonText = {
    translationKey: 'invite_add_to_calendar',
    text: 'Add to Calendar',
  };

  return (
    <>
      <div className="ResyInviteRsvp__head">
        <div className="ResyInviteRsvp__icon">
          <Icon className="ResyIcon ResyIcon--invite-green" iconClass="ResyIcon ResyIcon--invite-green" component={GreenCheck} aria-hidden="true" />
        </div>
        <TranslationProvider
          translationKey="invite_confirmed_header"
        >
          <h2 className="ResyInviteRsvp__title">
            All Set!
          </h2>
        </TranslationProvider>
        {!reservation.party.current_user_is_host
          && (
            <TranslationProvider
              translationKey="invite_confirmed_body"
              translationValues={{
                // eslint-disable-next-line camelcase
                host: reservation?.party?.host_first_name,
                serviceType,
                venue: reservation.venue.name,
              }}
            >
              <div className="ResyInviteRsvp__subtitle">
                We assume that
                {/* eslint-disable-next-line camelcase */}
                {reservation?.party?.host_first_name}
                {' '}
                is very much looking forward to seeing you.
              </div>
            </TranslationProvider>
          )}

        <hr />

        <div className="ResyInviteRsvp__venue-name">{reservation.venue.name}</div>
        <div className="ResyInviteRsvp__detail">
          <Icon className="ResyIcon ResyIcon--calendar" component={Calendar} iconClass="calendar" aria-hidden="true" />
          <div className="ResyInviteRsvp__detail-text">
            <span className="ResyInviteRsvp__detail-text__date">{reservation.reservation.date}</span>
            {' '}
            <TranslationProvider
              translationKey="at"
            >
              <span>at</span>
            </TranslationProvider>
            {' '}
            {reservation.reservation.time}
          </div>
        </div>
        <div className="ResyInviteRsvp__detail">
          <Icon className="ResyIcon ResyIcon--people" component={People} iconClass="people" aria-hidden="true" />
          {reservation.reservation.num_seats}
          {' '}
          <TranslationProvider
            translationKey="guests"
          >
            <div className="ResyInviteRsvp__detail-text">Guests</div>
          </TranslationProvider>
          {seating
            && (
            <>
              {`, ${seating}`}
            </>
            )}
        </div>
      </div>

      <div className="ResyInviteConfirmation">
        <div className="ResyInviteConfirmation__actions">
          <ButtonDropdown
            links={getCalendarLinks(reservation)}
            testId={testId}
            toggleButton={toggleButtonText}
          />

          {!reservation.party.current_user_is_host
            && (
              <TranslationProvider
                translationKey="invite_cancel"
              >
                <Button
                  data-test-id={`${testId}-button-cancel_invite`}
                  className="ResyInviteConfirmation__cancel-button"
                  onClick={toggleDeclineModal}
                  variant="secondary"
                >
                  Cancel My Invite
                </Button>
              </TranslationProvider>
            )}
        </div>

        <VenueLocationSummary
          location={reservation.venue.location}
          name={reservation.venue.name}
          phone={reservation.venue.contact.phone}
          url={reservation.venue.contact.url}
          social={reservation.venue.social}
          isMobile
          venue={reservation.venue}
        />
      </div>
    </>
  );
}

RSVPConfirmed.propTypes = {
  reservation: PropTypes.shape({
    links: PropTypes.shape({
      calendar: PropTypes.shape({
        google: PropTypes.string,
        ical: PropTypes.string,
        outlook: PropTypes.string,
      }),
    }),
    party: PropTypes.shape({
      current_user_confirmed: PropTypes.bool,
      current_user_is_host: PropTypes.bool,
      has_open_spots: PropTypes.bool,
      host_attendee_token: PropTypes.string,
      host_first_name: PropTypes.string,
    }),
    reservation: PropTypes.shape({
      date: PropTypes.string,
      date_time_iso: PropTypes.string,
      num_seats: PropTypes.number,
      service_type_id: PropTypes.number,
      time: PropTypes.string,
    }),
    venue: PropTypes.shape({
      contact: PropTypes.shape({
        phone: PropTypes.string,
        url: PropTypes.string,
      }),
      location: PropTypes.shape({
        coords: PropTypes.shape({
          lat: PropTypes.string,
          long: PropTypes.string,
        }),
      }),
      name: PropTypes.string,
      social: PropTypes.string,
    }),
    invite: PropTypes.shape({
      status_id: PropTypes.number,
    }),
  }).isRequired,
  seating: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  serviceType: PropTypes.string.isRequired,
  testId: PropTypes.string.isRequired,
  toggleDeclineModal: PropTypes.func.isRequired,
};

RSVPConfirmed.defaultProps = {
  seating: undefined,
};
